/** Creator "Record Your Vocal" helpers — mic devices, latency trim, take upload, take restore. */
import axios from "axios";
import { API } from "@/lib/creatorApi";
import { getUserToken } from "@/lib/userToken";

const MIC_KEY = "elpm-creator-mic";

export function getSavedMic() {
  try { return localStorage.getItem(MIC_KEY) || ""; } catch (e) { void e; return ""; }
}
export function saveMic(deviceId) {
  try { deviceId ? localStorage.setItem(MIC_KEY, deviceId) : localStorage.removeItem(MIC_KEY); } catch (e) { void e; }
}

/** Audio inputs. Labels are blank until the browser has granted mic permission once. */
export async function listInputDevices() {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const all = await navigator.mediaDevices.enumerateDevices();
  return all
    .filter((d) => d.kind === "audioinput")
    .map((d, i) => ({ deviceId: d.deviceId, label: d.label || (i === 0 ? "Built-in Microphone" : `Microphone ${i + 1}`), unlabeled: !d.label }));
}

/** Ask once for mic permission so device labels appear, then release the stream. */
export async function unlockMicLabels() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((t) => t.stop());
}

/** Drop `seconds` from the start of an AudioBuffer (latency compensation). */
export function trimHead(ctx, buffer, seconds) {
  const drop = Math.min(buffer.length - 1, Math.max(0, Math.round(seconds * buffer.sampleRate)));
  if (drop <= 0) return buffer;
  const out = ctx.createBuffer(buffer.numberOfChannels, buffer.length - drop, buffer.sampleRate);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.copyToChannel(buffer.getChannelData(c).subarray(drop), c);
  }
  return out;
}

/** Round-trip latency estimate: play-start gap + output + base latency. */
export function estimateHeadTrim(ctx, tRecStart, tPlayStart) {
  const gap = Math.max(0, (tPlayStart || 0) - (tRecStart || 0));
  return Math.max(0, gap + (ctx.outputLatency || 0) + (ctx.baseLatency || 0));
}

/** Peak (0–1) of an analyser's current time-domain frame. */
export function analyserPeak(analyser) {
  if (!analyser) return 0;
  const buf = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(buf);
  let peak = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = Math.abs(buf[i] - 128) / 128;
    if (v > peak) peak = v;
  }
  return peak;
}

/** Upload the original take blob to durable storage (shows up in My Projects → Recordings). */
export async function uploadVocalTake(blob, label) {
  const fd = new FormData();
  const ext = (blob.type || "").includes("mp4") ? "m4a" : (blob.type || "").includes("wav") ? "wav" : "webm";
  fd.append("file", blob, `voice-take.${ext}`);
  fd.append("label", label.slice(0, 160));
  fd.append("user_token", getUserToken());
  const { data } = await axios.post(`${API}/recording/take`, fd, { withCredentials: true });
  return data;
}

export function vocalFileUrl(vocal) {
  const u = vocal?.file_url;
  if (!u) return null;
  return u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

/**
 * Make sure a kept vocal take (song.vocal) sits on an engine channel.
 * Returns the channel index, or null when the song has no kept take.
 */
export async function ensureVocalTakeLoaded(engine, song) {
  const url = vocalFileUrl(song?.vocal);
  if (!url || !engine?.channels) return null;
  const existing = engine.channels.findIndex((c) => c?.buffer && c.sourceUrl === url);
  if (existing >= 0) return existing;
  const [idx] = engine.freeChannelIndices(1);
  if (idx == null) throw new Error("The studio is full — clear a channel first.");
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Couldn't fetch your recording (${res.status})`);
  const ab = await res.arrayBuffer();
  let buf = await engine.ctx.decodeAudioData(ab);
  if (song.vocal.trim_head_sec > 0) buf = trimHead(engine.ctx, buf, song.vocal.trim_head_sec);
  engine.loadBufferToChannel(idx, buf, song.vocal.label || `${song.title} · my voice`, url);
  return idx;
}
